import { Node as TiptapNode, ReactNodeViewRenderer, NodeViewWrapper, useEditorState, type NodeViewProps } from '@tiptap/react';
import type { Node as DocumentNode } from '@tiptap/pm/model';
import { Button } from '@/components/ui/button';

export function documentHeadings(doc: DocumentNode) {
  const headings: Array<{ position: number; level: number; title: string }> = [];
  doc.descendants((node, position) => {
    if (node.type.name === 'heading') headings.push({ position: position + 1, level: Number(node.attrs.level), title: node.textContent || 'Untitled heading' });
  });
  return headings;
}

export const DocumentPageBreak = TiptapNode.create({
  name: 'pageBreak', group: 'block', atom: true, selectable: true, isolating: true,
  parseHTML: () => [{ tag: 'div[data-page-break]' }],
  renderHTML: () => ['div', { 'data-page-break': '', role: 'separator', 'aria-label': 'Page break', class: 'my-6 border-t border-dashed text-center text-xs text-muted-foreground after:content-["Page_break"]' }],
  addKeyboardShortcuts() {
    return { 'Mod-Enter': () => this.editor.isEditable && this.editor.commands.insertContent({ type: this.name }) };
  }
});

function ContentsView({ editor, deleteNode }: NodeViewProps) {
  const headings = useEditorState({ editor, selector: ({ editor: active }) => documentHeadings(active.state.doc).filter(item => item.level <= 3) });
  return <NodeViewWrapper contentEditable={false} data-table-of-contents="" className="my-4 rounded-md border p-3">
    <div className="flex items-center justify-between gap-2"><strong>Table of contents</strong>{editor.isEditable && <Button type="button" size="sm" variant="ghost" aria-label="Remove table of contents" onClick={deleteNode}>Remove</Button>}</div>
    {headings.length ? <nav aria-label="Document table of contents" className="space-y-1 pt-2">{headings.map(heading =>
      <button key={heading.position} type="button" data-heading-level={heading.level}
        className="block w-full rounded px-1 text-left text-sm hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring data-[heading-level=2]:pl-5 data-[heading-level=3]:pl-9"
        onClick={() => editor.chain().focus().setTextSelection(heading.position).scrollIntoView().run()}>{heading.title}</button>
    )}</nav> : <p className="text-sm text-muted-foreground">Add headings to populate the table of contents.</p>}
  </NodeViewWrapper>;
}

/** Entries are derived from the current body; stale/generated headings never enter saved JSON. */
export const DocumentTableOfContents = TiptapNode.create({
  name: 'tableOfContents', group: 'block', atom: true, selectable: true, isolating: true,
  parseHTML: () => [{ tag: 'div[data-table-of-contents]' }],
  renderHTML: () => ['div', { 'data-table-of-contents': '' }, 'Table of contents'],
  addNodeView: () => ReactNodeViewRenderer(ContentsView)
});

export const DocumentLayoutToken = TiptapNode.create({
  name: 'layoutToken', group: 'inline', inline: true, atom: true,
  addAttributes: () => ({ kind: { default: 'title', parseHTML: element => element.getAttribute('data-layout-token') === 'pageNumber' ? 'pageNumber' : 'title' } }),
  parseHTML: () => [{ tag: 'span[data-layout-token]' }],
  renderHTML: ({ node }) => ['span', { 'data-layout-token': node.attrs.kind, class: 'rounded bg-muted px-1 text-xs' }, node.attrs.kind === 'pageNumber' ? 'Page number' : 'Document title']
});
